import React from "react";
import { Box, Skeleton } from "@mui/material";
import ReactECharts from "echarts-for-react";
import useChartTheme from "../useChartTheme";
import type { VisualizationMeta } from "hooks/useNLQuery";

interface Props {
  rows: Record<string, unknown>[];
  columns: string[];
  visualization: VisualizationMeta;
  loading?: boolean;
}

const AgenticScatterChart: React.FC<Props> = ({ rows, columns, visualization, loading }) => {
  const { baseAxis, baseTooltip, baseGrid, seriesColors, chartHeight } = useChartTheme();

  if (loading || !rows.length) {
    return <Skeleton variant="rounded" height={chartHeight} sx={{ borderRadius: 2 }} />;
  }

  const xCol = visualization.x_axis || columns.find((c) => c !== "patient_id") || columns[0];
  const yCol = visualization.y_axis || columns.find((c) => c !== xCol && c !== "patient_id") || columns[1];

  if (!xCol || !yCol) return null;

  const data = rows
    .map((r) => [Number(r[xCol] ?? 0), Number(r[yCol] ?? 0)])
    .filter(([x, y]) => !isNaN(x) && !isNaN(y));

  const option = {
    tooltip: {
      ...baseTooltip,
      trigger: "item" as const,
      formatter: (params: { value: number[] }) =>
        `${xCol.replace(/_/g, " ")}: ${params.value[0]}<br/>${yCol.replace(/_/g, " ")}: ${params.value[1]}`,
    },
    grid: baseGrid,
    xAxis: {
      ...baseAxis,
      type: "value" as const,
      name: xCol.replace(/_/g, " "),
      nameLocation: "middle" as const,
      nameGap: 28,
    },
    yAxis: {
      ...baseAxis,
      type: "value" as const,
      name: yCol.replace(/_/g, " "),
      nameLocation: "middle" as const,
      nameGap: 40,
    },
    series: [
      {
        type: "scatter" as const,
        data,
        symbolSize: 10,
        itemStyle: { color: seriesColors[0], opacity: 0.7 },
        emphasis: { itemStyle: { opacity: 1, shadowBlur: 8 } },
      },
    ],
  };

  return (
    <Box sx={{ width: "100%", height: chartHeight }}>
      <ReactECharts option={option} style={{ width: "100%", height: "100%" }} opts={{ renderer: "canvas" }} />
    </Box>
  );
};

export default AgenticScatterChart;
